import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import AdvancedCurrentPeopleFilter from 'views/Widgets/Filters/AdvancedSpecializedFilter/AdvancedCurrentPeopleFilter';
import { useAdvancedCurrentPeopleFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/context';
import {
  fromApiFilter,
  toApiFilter,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';
import { TGroup } from 'views/Widgets/Filters/types';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { dependencyNames } from './FeatureAudienceTable';
import {
  FeatureAudienceDependency,
  FeatureAudienceFlag,
  FeatureAudienceUpdate,
} from './types';
import { FeatureAudienceSaveError } from './useFeatureAudiences';

interface Props {
  flag: FeatureAudienceFlag | null;
  isOpen: boolean;
  onClose: () => void;
  // fires after the closing animation, when the page may drop the flag
  onClosed?: () => void;
  saveFlag: (update: FeatureAudienceUpdate) => Promise<FeatureAudienceFlag>;
  // flags that have no effect without this one
  dependents?: FeatureAudienceDependency[];
}

interface FormState {
  enabled: boolean;
  except_target_audience?: TGroup;
}

const FeatureAudienceEditModal: FC<Props> = ({
  flag,
  isOpen,
  onClose,
  onClosed,
  saveFlag,
  dependents = [],
}) => {
  const { formatMessage } = useIntl();
  const { suggestions, selectedSuggestionConverter } =
    useAdvancedCurrentPeopleFilter();
  const [object, setObject] = useState<FormState>({ enabled: false });

  // the form starts from the flag's stored global value and current exception
  useEffect(() => {
    if (isOpen && flag) {
      setObject({
        enabled: flag.enabled,
        except_target_audience: suggestions
          ? fromApiFilter(
              suggestions,
              selectedSuggestionConverter,
              flag.except_target_audience ?? undefined
            )
          : undefined,
      });
    }
  }, [isOpen, flag, suggestions, selectedSuggestionConverter]);

  const handleSubmit = useCallback(
    (submitted: FormState, onSubmit) => {
      if (!flag) return;
      saveFlag({
        section: flag.section,
        flag: flag.flag,
        enabled: submitted.enabled,
        except_target_audience:
          toApiFilter(submitted.except_target_audience) ?? null,
      })
        .then((saved) => onSubmit(saved))
        .catch((e: FeatureAudienceSaveError) =>
          // ValidatedForm reads field errors from error.data
          onSubmit(null, { data: e.fieldErrors ?? {} }, e.message)
        );
    },
    [flag, saveFlag]
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.SWITCH,
        name: 'enabled',
        // switchLabel is the label tied to the checkbox itself
        switchLabel: formatMessage({
          id: 'app.views.organization.feature_administration.edit.enabled_for_everyone',
          defaultMessage: 'Enabled for everyone',
        }),
      },
      {
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AdvancedCurrentPeopleFilter,
        name: 'except_target_audience',
        label: formatMessage({
          id: 'app.views.organization.feature_administration.edit.except',
          defaultMessage: 'Except',
        }),
        helperText: formatMessage({
          id: 'app.views.organization.feature_administration.edit.except_helper',
          defaultMessage:
            'Optional. People matching this filter get the opposite of the switch above. Leave it empty to apply the switch to everyone.',
        }),
      },
    ],
    [formatMessage]
  );

  if (!flag) {
    return null;
  }

  // same modal system and layout as the Create new cycle modal
  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={onClose}
      onClosed={onClosed}
      title={formatMessage(
        {
          id: 'app.views.organization.feature_administration.edit.title',
          defaultMessage: 'Edit {name}',
        },
        { name: flag.display_name }
      )}
      submitText={formatMessage({
        id: 'app.views.organization.feature_administration.edit.save',
        defaultMessage: 'Save',
      })}
      object={object}
      onChange={setObject}
      inputs={inputs}
      submitPromise={handleSubmit}
      callback={() => {}}
      buttonClassName=""
      renderForm={(
        inputsJsx: React.ReactNode,
        submitButton: React.ReactNode
      ) => (
        <>
          <p
            className="text-muted mb-2"
            data-testid="feature-audience-description"
          >
            {flag.description}
          </p>
          {flag.depends_on.length > 0 && (
            <p
              className="text-muted small mb-2"
              data-testid="feature-audience-edit-requires"
            >
              {formatMessage(
                {
                  id: 'app.views.organization.feature_administration.edit.requires',
                  defaultMessage:
                    'Requires {names}. This feature has no effect for anyone who has those switched off.',
                },
                { names: dependencyNames(flag.depends_on) }
              )}
            </p>
          )}
          {dependents.length > 0 && (
            <p
              className="text-muted small mb-2"
              data-testid="feature-audience-edit-dependents"
            >
              {formatMessage(
                {
                  id: 'app.views.organization.feature_administration.edit.dependents',
                  defaultMessage:
                    'Also required by {names}. Those features have no effect for anyone who has this one switched off.',
                },
                { names: dependencyNames(dependents) }
              )}
            </p>
          )}
          {inputsJsx}
          <Row className="mt-4">
            <Col className="col-md-6">
              <Button
                color="light"
                className="btn-block w-100"
                onClick={onClose}
              >
                {formatMessage({
                  id: 'app.views.organization.feature_administration.edit.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Button>
            </Col>
            <Col className="col-md-6">{submitButton}</Col>
          </Row>
        </>
      )}
    />
  );
};

export default React.memo(FeatureAudienceEditModal);
